import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../configurations/api';
import { tiktokCreatorInfoUrl } from '../configurations/api/url';
import { httpRequest } from '../redux/types';
import type { TiktokCreatorInfo } from '../types/tiktokDirectPost.types';

function unwrapCreatorInfo(payload: unknown): TiktokCreatorInfo | null {
    if (!payload || typeof payload !== 'object') return null;
    const root = payload as Record<string, unknown>;
    const data = (root.data ?? root) as Record<string, unknown>;
    const inner = (data.data ?? data) as TiktokCreatorInfo;
    if (!inner?.creator_username) return null;
    return inner;
}

export function useTiktokCreatorInfo(options: { enabled: boolean; mediaDurationSec?: number }) {
    const { enabled, mediaDurationSec } = options;
    const [creatorInfo, setCreatorInfo] = useState<TiktokCreatorInfo | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const requestIdRef = useRef(0);

    const refresh = useCallback(async () => {
        if (!enabled) return null;
        const requestId = ++requestIdRef.current;
        setLoading(true);
        setError(null);
        try {
            const query =
                mediaDurationSec != null && Number.isFinite(mediaDurationSec)
                    ? `?media_duration_sec=${encodeURIComponent(String(mediaDurationSec))}`
                    : '';
            const response = await api(tiktokCreatorInfoUrl + query, httpRequest.GET, null, 2);
            const info = unwrapCreatorInfo(response);
            if (requestId !== requestIdRef.current) return info;
            if (!info) throw new Error('Invalid creator info response');
            setCreatorInfo(info);
            return info;
        } catch (err: unknown) {
            if (requestId !== requestIdRef.current) return null;
            const msg =
                (err as { data?: { message?: string } })?.data?.message ??
                (err instanceof Error ? err.message : 'Could not load TikTok account info.');
            setError(msg);
            setCreatorInfo(null);
            return null;
        } finally {
            if (requestId === requestIdRef.current) setLoading(false);
        }
    }, [enabled, mediaDurationSec]);

    useEffect(() => {
        if (!enabled) {
            setCreatorInfo(null);
            setError(null);
            setLoading(false);
            return;
        }
        void refresh();
    }, [enabled, refresh]);

    return { creatorInfo, loading, error, refresh };
}
